'use client';
import { ReactNode } from 'react';
import Link from 'next/link';
import AssetImage from '@/components/ui/AssetImage';
import { useRouter } from 'next/navigation';
import { GEAR, GEAR_CAPTURE_MAP, FORMAT } from '@/utils/constants';
import { GearIcon, ICON_BG_COLOR_MAP } from '@/components/icons/Gear';
import { preferredModelsCount } from '@/utils/architecture';
import { FolderClosed, MessageCircle } from 'lucide-react';
import { DownloadIcon } from 'lucide-react';
import { toTonePathname } from '@/utils/helpers';
import { TimeAgo } from '@/components/ui/TimeAgo/TimeAgo';
import { formatCount } from '@/utils/format-count';
import { ToneCountWithUser } from '@/utils/helpers';
import { ToneCardUserStack } from './ToneCardUser';
import { Varified } from '@/components/icons/Varified';
import { TonePlayer } from '../Tones/Player/TonePlayer';
import { isPlayablePlatform } from '@/utils/player/to-player-tone';
import { useToneCardTracking } from '../ToneSection/ToneSurface';

interface Props {
  children?: ReactNode;
  action?: ReactNode;
  tone: ToneCountWithUser;
  className?: string;
  /** Index within its row, for click-through tracking. */
  position?: number;
}

function ToneCardStackFC({ children, tone, className, position }: Props) {
  const router = useRouter();
  const href = `/tones/${toTonePathname(tone)}`;
  const userHref = `/${tone.user.username}`;
  // const platform = tone.platform as FORMAT;
  const gear = tone.gear as GEAR;
  const imageUrl = tone.images && tone.images.length > 0 ? tone.images[0] : undefined;

  const publishedAt = tone.published_at || tone.created_at || undefined;
  const username = tone.user.username || '';
  const avatarUrl = tone.user.avatar_url || '';
  const downloadsCount = tone.downloads_count || 0;
  // Core-only surface, so always the A2-defacto count.
  const modelsCount = preferredModelsCount(tone);
  const commentsCount = tone.visible_comments_count || 0;
  const title = tone.title || '';

  const isUserVarified = !!tone.user.is_verified;
  const tracking = useToneCardTracking(tone.id, position, href);

  const handleCardClick = (e: React.MouseEvent) => {
    // Only handle clicks if they didn't originate from a link or button
    const target = e.target as HTMLElement;
    const isLinkClick = target.closest('a') || target.closest('button');

    if (!isLinkClick && href) {
      router.push(href);
    }
  };

  const renderImage = () => {
    if (imageUrl) {
      return (
        <div className="relative w-full aspect-square rounded-t-2xl overflow-hidden">
          <AssetImage src={imageUrl} alt={title} fill className="object-cover" sizes="300px" />
        </div>
      );
    }

    return (
      <div
        className="relative w-full aspect-square rounded-t-2xl flex items-center justify-center"
        style={{ backgroundColor: ICON_BG_COLOR_MAP[gear] }}
      >
        <GearIcon size={80} gear={gear} isColor={true} />
      </div>
    );
  };

  return (
    <div
      onClick={handleCardClick}
      {...tracking}
      className={`relative overflow-hidden w-full max-w-sm h-full bg-zinc-900 rounded-2xl flex flex-col ${className} border border-zinc-700 rounded-b-2xl`}
    >
      {/* Image Section */}
      {href ? (
        <Link href={href} className="block">
          {renderImage()}
        </Link>
      ) : (
        renderImage()
      )}

      {/* Content Section */}
      <div className="px-4 py-6 flex flex-col flex-1 justify-between space-y-4">
        {/* Player + TitleSection */}
        <div className="flex flex-col space-y-4">
          {/* Slim Player */}
          <div className="flex items-center justify-center">
            <TonePlayer
              id={tone.id!.toString()}
              layout="mini"
              disabled={
                !isPlayablePlatform(tone.platform) ||
                (tone.platform === FORMAT.NAM && (tone.a2_models_count ?? 0) === 0)
              }
              tones={[{ tone, lazy: 'featured' }]}
            />
          </div>
          {/* Title */}
          <div>
            {href ? (
              <Link href={href}>
                <h3 className="text-base font-semibold text-white line-clamp-2 text-center">
                  {title}
                </h3>
              </Link>
            ) : (
              <h3 className="text-base font-semibold text-white line-clamp-2 text-center">
                {title}
              </h3>
            )}
          </div>
        </div>

        {/* Gear + Stats Section */}
        <div className="flex flex-col space-y-4">
          <div className="flex gap-2 items-center justify-center">
            {/* <div className="flex flex-shrink-0 items-center">
            <GearIcon size={20} gear={gear} />
          </div> */}
            <p
              className={`text-zinc-400 text-xs text-center sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis`}
            >
              {`${GEAR_CAPTURE_MAP[gear]}`}
            </p>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center gap-4 text-zinc-400 text-sm">
            <div className="flex items-center gap-1">
              <DownloadIcon className="w-4 h-4" />
              <span>{formatCount(downloadsCount)}</span>
            </div>
            <div className="flex items-center gap-1">
              <FolderClosed className="w-4 h-4" />
              <span>{modelsCount != null ? modelsCount.toLocaleString() : '—'}</span>
            </div>
            {commentsCount > 0 && (
              <div className="flex items-center gap-1">
                <MessageCircle className="w-4 h-4" />
                <span>{formatCount(commentsCount)}</span>
              </div>
            )}
          </div>
          {/* Bottom Section - User Info */}
          <div className="flex items-center justify-center gap-2">
            <ToneCardUserStack
              hideUsername={true}
              username={username}
              avatarUrl={avatarUrl}
              userHref={userHref}
            />
            {isUserVarified && (
              <Varified size={20} className="-ml-[16px] -mb-[12px] z-10 pointer-events-none" />
            )}
            <TimeAgo
              date={publishedAt || ''}
              className="text-zinc-400 text-xs inline-block min-w-[2rem] text-center"
            />
          </div>
        </div>
      </div>

      {children && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

const ToneCardStack = ({ children, action, tone, className, position }: Props) => {
  return (
    <div className={`flex w-full items-start gap-4 ${className}`}>
      <ToneCardStackFC tone={tone} position={position}>
        {children}
      </ToneCardStackFC>
      {!!action && action}
    </div>
  );
};

export default ToneCardStack;
